import * as React from "react"
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Circle,
    Loader2,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { LucideBulletIcon } from "./LucideBulletIcon"
import { cn } from "@/lib/utils"
import {
    agreedDone,
    agreedText,
    bulletIcon,
    bulletText,
    formatTimecode,
    type CopilotAgreedItemLike,
    type CopilotBreakdownItem,
    type CopilotSummary,
    type CopilotSummaryBulletLike,
    type ProfileStatus,
} from "../../types"
import { useCopilotSessionSummary } from "../../hooks/useCopilotSessionSummary"
import { StatusPill } from "../StatusPill"

function TopicChip({ item }: { item: CopilotSummaryBulletLike }) {
    const text = bulletText(item)
    if (!text) return null
    const name = bulletIcon(item) || "circle-dot"
    return (
        <span className="inline-flex max-w-full items-center gap-1.5 rounded-m border border-neutral-border-300 bg-neutral-background-100 px-2.5 py-1.5 text-caption text-neutral-text-900">
            <LucideBulletIcon
                name={name}
                className="size-3.5 shrink-0 text-neutral-text-500"
            />
            <span className="truncate">{text}</span>
        </span>
    )
}

function ClientInfoRow({ item }: { item: CopilotSummaryBulletLike }) {
    const text = bulletText(item)
    if (!text) return null
    const name = bulletIcon(item) || "circle-dot"
    return (
        <li className="text-caption text-neutral-text-900 flex gap-2">
            <span className="text-neutral-text-400 shrink-0 mt-0.5 flex size-3.5 items-center justify-center">
                <LucideBulletIcon name={name} className="size-3.5" />
            </span>
            <span>{text}</span>
        </li>
    )
}

function AgreedRow({ item }: { item: CopilotAgreedItemLike }) {
    const text = agreedText(item)
    if (!text) return null
    const done = agreedDone(item)
    return (
        <li className="text-caption text-neutral-text-900 flex gap-2">
            <span className="shrink-0 mt-0.5 flex size-3.5 items-center justify-center">
                {done ? (
                    <CheckCircle2 className="size-3.5 text-state-positive" strokeWidth={2.5} aria-hidden />
                ) : (
                    <Circle className="size-3.5 text-neutral-text-400" strokeWidth={2.5} aria-hidden />
                )}
            </span>
            <span>{text}</span>
        </li>
    )
}

function FollowUpRow({ text }: { text: string }) {
    if (!text.trim()) return null
    return (
        <li className="text-caption text-neutral-text-900 flex gap-2">
            <span className="shrink-0 mt-0.5 flex size-3.5 items-center justify-center">
                <AlertTriangle className="size-3.5 text-state-warning" strokeWidth={2.5} aria-hidden />
            </span>
            <span>{text}</span>
        </li>
    )
}

function BreakdownList({
    items,
    onSeekToTimecode,
    registerTourTarget,
}: {
    items: CopilotBreakdownItem[]
    onSeekToTimecode?: (seconds: number) => void
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}) {
    const listRef = React.useCallback(
        (el: HTMLUListElement | null) => {
            registerTourTarget?.("meeting-timecodes", el)
        },
        [registerTourTarget],
    )

    return (
        <ul
            ref={registerTourTarget ? listRef : undefined}
            data-tour-target={registerTourTarget ? "meeting-timecodes" : undefined}
            className="space-y-3 mt-2"
        >
            {items.map((item, i) => {
                const label = formatTimecode(item.t_sec)
                return (
                    <li key={`${item.t_sec}-${i}`} className="flex flex-col gap-0.5">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            {onSeekToTimecode ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="xs"
                                    className="h-auto p-0 text-caption font-medium text-primary-600 underline-offset-2 hover:bg-transparent hover:underline hover:text-primary-600"
                                    onClick={() => onSeekToTimecode(item.t_sec)}
                                >
                                    {label}
                                </Button>
                            ) : (
                                <span className="text-caption font-medium text-neutral-text-500">{label}</span>
                            )}
                            <span className="text-caption font-medium text-neutral-text-900">
                                {item.title}
                            </span>
                        </div>
                        {item.detail ? (
                            <p className="text-caption text-neutral-text-500 pl-0 sm:pl-0">{item.detail}</p>
                        ) : null}
                    </li>
                )
            })}
        </ul>
    )
}

function SummaryTitleLeft() {
    return (
        <div className="flex items-center gap-2">
            <span className="size-3.5 shrink-0 flex items-center justify-center">
                <span className="size-1.5 rounded-full bg-primary-400 dark:bg-primary-600" />
            </span>
            <span className="text-caption font-medium text-neutral-text-800">
                Итоги встречи
            </span>
        </div>
    )
}

interface RecordSummarySectionProps {
    copilotSessionId: string
    initialSummary?: CopilotSummary
    isFinalizing?: boolean
    summaryFailed?: boolean
    className?: string
    onSeekToTimecode?: (seconds: number) => void
    /** When true, register tour targets (use only for the latest demo record). */
    registerTourTargets?: boolean
    registerTourTarget?: (id: string, el: HTMLElement | null) => void
}

export function RecordSummarySection({
    copilotSessionId,
    initialSummary,
    isFinalizing,
    summaryFailed,
    className,
    onSeekToTimecode,
    registerTourTargets = false,
    registerTourTarget,
}: RecordSummarySectionProps) {
    const [isOpen, setIsOpen] = React.useState(Boolean(initialSummary) && !isFinalizing)
    const [breakdownOpen, setBreakdownOpen] = React.useState(false)
    const [summary, setSummary] = React.useState<CopilotSummary | null>(initialSummary ?? null)
    const [wantFetch, setWantFetch] = React.useState(false)
    const [prevInitialSummary, setPrevInitialSummary] = React.useState(initialSummary ?? null)
    const [sawFinalizing, setSawFinalizing] = React.useState(
        Boolean(isFinalizing) || Boolean(summaryFailed),
    )
    const [clearedSummaryFailed, setClearedSummaryFailed] = React.useState(false)

    if ((isFinalizing || summaryFailed) && !sawFinalizing) {
        setSawFinalizing(true)
    }

    if (initialSummary && initialSummary !== prevInitialSummary) {
        setPrevInitialSummary(initialSummary)
        setSummary(initialSummary)
        // Historic hydrate opens; just-finished generation stays collapsed.
        if (!sawFinalizing) {
            setIsOpen(true)
        }
    }

    const summaryQuery = useCopilotSessionSummary(copilotSessionId, {
        enabled: wantFetch && !summary && !isFinalizing,
    })

    React.useEffect(() => {
        if (summaryQuery.data) {
            setSummary(summaryQuery.data)
            setClearedSummaryFailed(true)
        }
    }, [summaryQuery.data])

    const isFetching = summaryQuery.isFetching
    const effectiveSummaryFailed =
        Boolean(summaryFailed) && !summary && !clearedSummaryFailed
    const fetchFailed =
        (wantFetch && summaryQuery.isError && !summary) || effectiveSummaryFailed

    const handleToggle = React.useCallback(async () => {
        if (isFinalizing) return
        const next = !isOpen
        setIsOpen(next)
        if (next && !summary) {
            setWantFetch(true)
        }
    }, [isFinalizing, isOpen, summary])

    const handleRetry = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        setWantFetch(true)
        void summaryQuery.refetch()
    }, [summaryQuery])

    const tourRegister = registerTourTargets ? registerTourTarget : undefined

    const resultsButtonRef = React.useCallback(
        (el: HTMLButtonElement | null) => {
            tourRegister?.("meeting-results", el)
        },
        [tourRegister],
    )
    const resultsBodyRef = React.useCallback(
        (el: HTMLDivElement | null) => {
            tourRegister?.("meeting-results-body", el)
        },
        [tourRegister],
    )
    const breakdownButtonRef = React.useCallback(
        (el: HTMLButtonElement | null) => {
            tourRegister?.("meeting-breakdown", el)
        },
        [tourRegister],
    )

    const topics = summary?.topics ?? []
    const clientInfo = summary?.client_info ?? []
    const agreed = summary?.agreed ?? []
    const followUp = summary?.follow_up ?? []
    const breakdown = summary?.breakdown ?? []
    const hasBreakdown = breakdown.length > 0

    let headerStatus: ProfileStatus | null = null
    if (isFinalizing) {
        headerStatus = "processing"
    } else if (effectiveSummaryFailed) {
        headerStatus = "error"
    } else if (sawFinalizing && summary) {
        headerStatus = "success"
    }

    const statusPill =
        headerStatus == null ? null : (
            <StatusPill
                status={headerStatus}
                label={headerStatus === "success" ? "Анализ завершен" : undefined}
            />
        )

    const headerRight = isFinalizing ? (
        statusPill
    ) : (
        <span className="flex shrink-0 items-center gap-1 text-neutral-text-500">
            {statusPill}
            {isOpen
                ? <ChevronUp className="size-3.5" strokeWidth={2.5} />
                : <ChevronDown className="size-3.5" strokeWidth={2.5} />}
        </span>
    )

    return (
        <div className={cn("rounded-s border border-neutral-border-300 overflow-hidden", className)}>
            {isFinalizing ? (
                <div className="flex h-auto w-full items-center justify-between gap-2 px-3 py-2 text-left">
                    <SummaryTitleLeft />
                    {headerRight}
                </div>
            ) : (
                <Button
                    ref={tourRegister ? resultsButtonRef : undefined}
                    type="button"
                    variant="ghost"
                    onClick={() => void handleToggle()}
                    data-tour-target={tourRegister ? "meeting-results" : undefined}
                    className="flex h-auto w-full items-center justify-between gap-2 rounded-none px-3 py-2 text-left hover:bg-neutral-background-100"
                >
                    <SummaryTitleLeft />
                    {headerRight}
                </Button>
            )}

            {isOpen && !isFinalizing && (
                <div
                    ref={tourRegister ? resultsBodyRef : undefined}
                    data-tour-target={tourRegister ? "meeting-results-body" : undefined}
                    className="px-3 pb-3 pt-1 border-t border-neutral-border-300"
                >
                    {isFetching ? (
                        <div className="flex items-center justify-center py-3">
                            <Loader2 className="size-4 animate-spin text-neutral-text-500" />
                        </div>
                    ) : summary ? (
                        <div className="space-y-4 mt-1">
                            {topics.length > 0 && (
                                <div>
                                    <div className="text-caption font-medium text-neutral-text-500 mb-1.5">Темы</div>
                                    <div className="flex flex-wrap gap-2">
                                        {topics.map((item, i) => (
                                            <TopicChip key={i} item={item} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(clientInfo.length > 0 || agreed.length > 0 || followUp.length > 0) && (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    {clientInfo.length > 0 && (
                                        <div>
                                            <div className="text-caption font-medium text-neutral-text-500 mb-1">О клиенте</div>
                                            <ul className="space-y-1.5">
                                                {clientInfo.map((item, i) => (
                                                    <ClientInfoRow key={i} item={item} />
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {agreed.length > 0 && (
                                        <div>
                                            <div className="text-caption font-medium text-neutral-text-500 mb-1">Договорились</div>
                                            <ul className="space-y-1.5">
                                                {agreed.map((item, i) => (
                                                    <AgreedRow key={i} item={item} />
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {followUp.length > 0 && (
                                        <div>
                                            <div className="text-caption font-medium text-neutral-text-500 mb-1">Осталось обсудить</div>
                                            <ul className="space-y-1.5">
                                                {followUp.map((text, i) => (
                                                    <FollowUpRow key={i} text={String(text)} />
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {hasBreakdown && (
                                <div className="rounded-s border border-neutral-border-300 overflow-hidden">
                                    <Button
                                        ref={tourRegister ? breakdownButtonRef : undefined}
                                        type="button"
                                        variant="ghost"
                                        onClick={() => setBreakdownOpen((v) => !v)}
                                        data-tour-target={tourRegister ? "meeting-breakdown" : undefined}
                                        className="flex h-auto w-full items-center justify-between gap-2 rounded-none px-3 py-2 text-left hover:bg-neutral-background-100"
                                    >
                                        <span className="text-caption font-medium text-neutral-text-800">
                                            Разбор встречи
                                        </span>
                                        <span className="text-neutral-text-500 shrink-0">
                                            {breakdownOpen
                                                ? <ChevronUp className="size-3.5" strokeWidth={2.5} />
                                                : <ChevronDown className="size-3.5" strokeWidth={2.5} />}
                                        </span>
                                    </Button>
                                    {breakdownOpen && (
                                        <div className="px-3 pb-3 border-t border-neutral-border-300">
                                            <BreakdownList
                                                items={breakdown}
                                                onSeekToTimecode={onSeekToTimecode}
                                                registerTourTarget={tourRegister}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : fetchFailed ? (
                        <div className="py-3 flex flex-col items-center gap-2">
                            <span className="text-caption text-neutral-text-500">
                                Не удалось загрузить итоги.
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="xs"
                                onClick={handleRetry}
                                className="h-auto p-0 text-caption text-primary-600 underline underline-offset-2 hover:bg-transparent hover:text-primary-600"
                            >
                                Повторить
                            </Button>
                        </div>
                    ) : (
                        <div className="py-3 text-caption text-neutral-text-500 text-center">
                            Итоги недоступны
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
